import React, { useState, useEffect } from "react";
import { useForm, Create } from "@refinedev/antd";
import { useList } from "@refinedev/core";
import { PROCUREMENT_PLAN_LIST_QUERY } from "../procurement-plan/queries";
// Removed gql import - using existing queries
import { PROCUREMENT_PLAN_REPORT_CREATE_MUTATION } from "./queries";
import { IProcurementPlanReport, TeamApprovalStatus } from "./interface";
import { Form, Input, Select, Button, Card, Row, Col, Alert, Spin, Space, Typography, Divider, List, Tag } from "antd";
// Removed Apollo Client import - using RefineJS approach instead
import { TEAM_LIST_QUERY } from "../teams/queries";
import { REQUEST_TEMPLATE_LIST_QUERY } from "../purchase-template/queries";

const { Title, Text } = Typography;

export const CreateProcurementPlanReport: React.FC = () => {
  const { formProps, saveButtonProps, onFinish } = useForm<IProcurementPlanReport>({
    resource: "procurementPlanReports",
    meta: {
      gqlMutation: PROCUREMENT_PLAN_REPORT_CREATE_MUTATION,
    }
  });

  const [year, setYear] = useState<number>(new Date().getFullYear());
  const [templateId, setTemplateId] = useState<string | null>(null);
  const [isCheckingPlans, setIsCheckingPlans] = useState(false);
  const [approvalStatus, setApprovalStatus] = useState<TeamApprovalStatus[]>([]);
  const [showWarnings, setShowWarnings] = useState(false);
  const [hasApprovedPlans, setHasApprovedPlans] = useState(false);
  const [hasUnapprovedPlans, setHasUnapprovedPlans] = useState(false);

  // Fetch teams
  const { data: teamsData, isLoading: teamsLoading } = useList<any>({
    resource: "teams",
    meta: { gqlQuery: TEAM_LIST_QUERY },
  });
  const teams = Array.isArray(teamsData?.data) ? teamsData?.data : (teamsData?.data as any)?.teams?.nodes || [];

  // Fetch templates of type PLAN_REPORT
  const { data: templatesData, isLoading: templatesLoading } = useList<any>({
    resource: "purchaseTemplates",
    meta: {
      gqlQuery: REQUEST_TEMPLATE_LIST_QUERY,
      variables: {
        query: {
          filter: { templateKind: "PLAN_REPORT", isActive: true },
          paging: { limit: 10, offset: 0 },
          sorting: []
        }
      }
    },
  });
  const templates = ((templatesData?.data && !Array.isArray(templatesData.data)) ? (templatesData.data as any)?.purchaseTemplates?.nodes || [] : templatesData?.data || [])
    .filter((t: any) => t.templateKind === 'PLAN_REPORT');

  // Auto-select template if there's only one available
  useEffect(() => {
    if (templates.length === 1 && !templateId) {
      const defaultTemplate = templates[0];
      setTemplateId(defaultTemplate.id);
      formProps.form?.setFieldsValue({ templateId: defaultTemplate.id });
    }
  }, [templates, templateId, formProps.form]);

  // Initialize form with current year
  useEffect(() => {
    formProps.form?.setFieldsValue({ year });
  }, [year, formProps.form]);

  // Use existing teams query data and handle approved plans differently
  // We'll fetch all procurement plans and filter client-side
  const { data: allPlansData, isLoading: allPlansLoading } = useList<any>({
    resource: "procurementPlans",
    meta: {
      gqlQuery: PROCUREMENT_PLAN_LIST_QUERY
    },
    pagination: { mode: "off" },
    filters: [],
    sorters: []
  });

  // Process approval status when data changes
  useEffect(() => {
    if (allPlansData && teams.length > 0 && year) {
      const allPlans = Array.isArray(allPlansData?.data) ? allPlansData.data : [];
      // Filter for approved plans in the selected year
      const approvedPlans = allPlans.filter((plan: any) => 
        plan.status === 'APPROVED' && plan.fiscalYear === year
      );
      processApprovalStatus(approvedPlans);
    }
  }, [allPlansData, teams, year]);

  const processApprovalStatus = (approvedPlans: any[]) => {
    setIsCheckingPlans(true);
    setShowWarnings(false);
    
    try {
      const allTeams = teams; // Use the teams data from the useList hook

      // Build approval status for each team
      const status: TeamApprovalStatus[] = allTeams.map((team: any) => {
        const teamPlans = approvedPlans.filter((plan: any) => plan.teamId === team.id);
        const approvedTeamPlans = teamPlans.filter((plan: any) => plan.status === 'APPROVED');
        
        return {
          teamId: team.id,
          teamName: team.name,
          hasApprovedPlans: approvedTeamPlans.length > 0,
          planCount: teamPlans.length,
          approvedPlanCount: approvedTeamPlans.length
        };
      });

      setApprovalStatus(status);
      
      // Check if we have any approved plans
      const hasAnyApproved = status.some(team => team.hasApprovedPlans);
      const hasAnyUnapproved = status.some(team => team.planCount > 0 && !team.hasApprovedPlans);
      
      setHasApprovedPlans(hasAnyApproved);
      setHasUnapprovedPlans(hasAnyUnapproved);
      setShowWarnings(true);
      
    } catch (error) {
      console.error("Error processing approval status:", error);
    } finally {
      setIsCheckingPlans(false);
    }
  };

  const handleSubmit = async (values: any) => {
    // Check if we should allow submission based on validation
    if (showWarnings && !hasApprovedPlans) {
      // Don't allow submission if no approved plans exist
      return;
    }
    
    // Get all approved plans for the selected year
    const allPlans = Array.isArray(allPlansData?.data) ? allPlansData.data : [];
    const approvedPlans = allPlans.filter((plan: any) =>
      plan.status === 'APPROVED' && plan.fiscalYear === year
    );
    
    // Create basic plan summaries (backend will fetch detailed plans with items)
    const planSummaries = approvedPlans.map((plan: any) => ({
      procurementPlanId: plan.id,
      teamId: plan.teamId,
      teamName: plan.teamName,
      totalAmount: plan.totalAmount,
      itemCount: 0, // Will be updated by backend
      capturedAt: new Date(),
      items: [] // Will be populated by backend
    }));
    
    // Submit with plan summaries - backend will enhance with items
    onFinish({
      ...values,
      plans: planSummaries
    });
  };

  const handleYearChange = (value: number | null) => {
    if (value !== null) {
      setYear(value);
      formProps.form?.setFieldsValue({ year: value });
    }
  };

  const handleTemplateChange = (value: string | null) => {
    setTemplateId(value);
    formProps.form?.setFieldsValue({ templateId: value });
  };

  return (
    <Create
      saveButtonProps={saveButtonProps}
      footerButtons={() => null}
      title="Create Procurement Plan Report"
    >
      <Card>
        <Form {...formProps} layout="vertical" onFinish={handleSubmit}>
          <Row gutter={[16, 16]}>
            <Col span={12}>
              <Form.Item
                label="Year"
                name="year"
                rules={[{ required: true, message: 'Year is required' }]}
              >
                <Input
                  type="number"
                  placeholder="Enter fiscal year"
                  onChange={(e) => handleYearChange(parseInt(e.target.value) || null)}
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label="Template (PLAN_REPORT)"
                name="templateId"
              >
                <Select
                  placeholder="Select a template"
                  loading={templatesLoading}
                  onChange={handleTemplateChange}
                  allowClear
                  notFoundContent={templatesLoading ? "Loading templates..." : "No PLAN_REPORT templates found"}
                >
                  {templates.map((template: any) => (
                    <Select.Option key={template.id} value={template.id}>
                      {template.name}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>

          {/* Validation Warnings Section */}
          {showWarnings && (
            <div style={{ marginTop: 24 }}>
              <Divider />
              <Title level={4}>Approval Status Validation</Title>
              
              {!hasApprovedPlans && (
                <Alert
                  message="No approved procurement plans found for the selected year"
                  description="You cannot create a report without approved plans. Please ensure teams have approved procurement plans for this year."
                  type="error"
                  showIcon
                  style={{ marginBottom: 16 }}
                />
              )}

              {hasUnapprovedPlans && hasApprovedPlans && (
                <Alert
                  message="Some teams have plans but they are not approved yet"
                  description="The report will only include approved plans. Teams with unapproved plans will be excluded."
                  type="warning"
                  showIcon
                  style={{ marginBottom: 16 }}
                />
              )}

              {hasApprovedPlans && (
                <Alert
                  message="Approved plans found"
                  description="You can proceed with creating the procurement plan report."
                  type="success"
                  showIcon
                  style={{ marginBottom: 16 }}
                />
              )}

              {/* Detailed team status */}
              {approvalStatus.length > 0 && (
                <Card title="Team Approval Status" style={{ marginTop: 16 }}>
                  <List
                    dataSource={approvalStatus}
                    renderItem={(team) => (
                      <List.Item>
                        <Space>
                          <Text strong>{team.teamName}</Text>
                          {team.hasApprovedPlans ? (
                            <Tag color="success">Approved Plans: {team.approvedPlanCount}</Tag>
                          ) : team.planCount > 0 ? (
                            <Tag color="warning">Unapproved Plans: {team.planCount}</Tag>
                          ) : (
                            <Tag color="default">No Plans</Tag>
                          )}
                        </Space>
                      </List.Item>
                    )}
                  />
                </Card>
              )}
            </div>
          )}

          {(isCheckingPlans || allPlansLoading) && (
            <div style={{ textAlign: 'center', margin: '24px 0' }}>
              <Spin tip="Checking approved plans..." />
            </div>
          )}

          <Divider />
          <Space style={{ marginTop: 24 }}>
            <Button type="primary" htmlType="submit" 
                    disabled={!hasApprovedPlans || !year}>
              Create Report
            </Button>
            <Button type="default" onClick={() => formProps.form?.resetFields()}>
              Reset
            </Button>
          </Space>
        </Form>
      </Card>
    </Create>
  );
};